import { formatIsoDate } from '@if/core/utils'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WatchLaterIcon from '@mui/icons-material/WatchLater'
import { Box, Tooltip } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { EMetro2ReportingReason } from '@src/types/constants'
import { Metro2Reporting } from '@src/types/loan/loan-calendar'
import { ReactNode, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export const useMetro2DialogColumns = (): GridColDef<Metro2Reporting>[] => {
  const { t } = useTranslation()

  const displayCorrectPaymentStatusIcon = useCallback(
    (metro2Reporting: Metro2Reporting) => {
      let content: ReactNode
      let tooltipTitle: string

      if (metro2Reporting.reported) {
        tooltipTitle = t(`loan.payment.creditReportDialog.reported`)
        content = <CheckCircleIcon color="success" />
      } else {
        tooltipTitle = t(`loan.payment.creditReportDialog.notReported`)
        content = <WatchLaterIcon color="action" />
      }
      return (
        <Tooltip title={tooltipTitle}>
          <Box display="flex" alignItems="center" justifyContent="center" height="100%">
            {content}
          </Box>
        </Tooltip>
      )
    },
    [t],
  )

  return useMemo<GridColDef<Metro2Reporting>[]>(
    () => [
      {
        field: 'metro2OverrideDate',
        width: 160,
        headerName: t('loan.payment.creditReportDialog.date'),
        editable: false,
        filterable: false,
        type: 'date',
        valueFormatter: formatIsoDate,
      },
      {
        field: 'metro2ReportingReason',
        headerName: t('loan.payment.creditReportDialog.reason'),
        width: 240,
        editable: false,
        filterable: false,
        type: 'singleSelect',
        valueOptions: [
          {
            value: EMetro2ReportingReason.IcebergError,
            label: t(`enum.EMetro2ReportingReason.${EMetro2ReportingReason.IcebergError}`),
          },
          {
            value: EMetro2ReportingReason.ClientChance,
            label: t(`enum.EMetro2ReportingReason.${EMetro2ReportingReason.ClientChance}`),
          },
        ] as { value: EMetro2ReportingReason; label: string }[],
        valueFormatter: (value: EMetro2ReportingReason): string => t(`enum.EMetro2ReportingReason.${value}`),
      },
      {
        field: 'note',
        headerName: t('loan.payment.creditReportDialog.desc'),
        editable: false,
        filterable: false,
        flex: 1,
      },
      {
        field: 'createdByUserFullname',
        headerName: t('loan.payment.creditReportDialog.createdBy'),
        editable: false,
        filterable: false,
        flex: 1,
        valueFormatter: (value) => (value == null ? t('loan.payment.creditReportDialog.system') : value),
      },
      {
        field: 'createdOn',
        width: 160,
        headerName: t('loan.payment.creditReportDialog.createdOn'),
        editable: false,
        filterable: false,
        type: 'date',
        valueFormatter: formatIsoDate,
      },
      {
        field: 'reported',
        width: 50,
        headerName: '',
        editable: false,
        renderCell: (params: GridRenderCellParams<Metro2Reporting>) => displayCorrectPaymentStatusIcon(params.row),
      },
    ],
    [t, displayCorrectPaymentStatusIcon],
  )
}
